// Datas para quem le conversas. Duas regras que valem para todo o visualizador:
// nunca segundos, porque a lista atualiza de dez em dez segundos e "ha 8
// segundos" e ruido, e nunca AM/PM, porque o operador da RP le horas de 0 a 23.

// Maior unidade que cabe, sempre uma so, que e o que se le de relance numa
// lista. Sem semanas: "ha 10 dias" e depois "ha 1 mes", como o Chatwoot faz
// (formatDistanceToNow do date-fns: dias ate aos 30, meses ate ao ano).
const MINUTE = 60;

const UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["day", 86400],
  ["hour", 3600],
  ["minute", MINUTE],
];

// Os formatadores do Intl sao caros de construir e sao chamados uma vez por
// linha da lista, portanto ficam em cache por locale.
const caches = { relative: new Map(), now: new Map(), absolute: new Map(), clock: new Map(), day: new Map(), shortDay: new Map() };

const cached = (kind, language, build) => {
  const cache = caches[kind];
  if (!cache.has(language)) cache.set(language, build());
  return cache.get(language);
};

const parse = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "ha 2 horas", "ha 1 dia", "ha 10 dias", "ha 2 meses": sempre com o numero,
// nunca "ontem" nem "semana passada". E a forma do Chatwoot (dynamicTime, que e
// o formatDistanceToNow do date-fns com sufixo) e e a que o operador ja le no
// resto do dia; o numeric "always" do Intl da-a em cada lingua sem chaves de
// traducao nossas.
export const formatRelative = (iso, language) => {
  const date = parse(iso);
  if (!date) return "";
  const diffSeconds = (date.getTime() - Date.now()) / 1000;
  const magnitude = Math.abs(diffSeconds);
  // Abaixo de um minuto: "agora" / "now". A unica palavra sem numero, porque
  // "ha 0 segundos" nao e uma hora e a lista atualiza de dez em dez segundos.
  // O minuto e a menor unidade da lista, portanto este e o chao dela.
  if (magnitude < MINUTE) {
    const now = cached("now", language, () => new Intl.RelativeTimeFormat(language, { numeric: "auto" }));
    return now.format(0, "second");
  }
  const rtf = cached("relative", language, () => new Intl.RelativeTimeFormat(language, { numeric: "always" }));
  // A unidade e a maior cuja contagem ARREDONDADA ja chega a um. Escolher a
  // unidade pelo piso e contar com o arredondamento davam duas respostas
  // diferentes a mesma pergunta: 3599 segundos saiam como "ha 60 minutos",
  // 86390 como "ha 24 horas", 2591000 como "ha 30 dias" e 31530000 como "ha
  // 12 meses", todas elas a unidade seguinte escrita por extenso.
  // O ciclo sai sempre: acima de um minuto, o minuto ja arredonda para um.
  for (const [unit, seconds] of UNITS) {
    const value = Math.round(diffSeconds / seconds);
    if (Math.abs(value) >= 1) return rtf.format(value, unit);
  }
};

// Data e hora completas, para o title de um tempo relativo.
export const formatAbsolute = (iso, language) => {
  const date = parse(iso);
  if (!date) return "";
  const formatter = cached(
    "absolute",
    language,
    () => new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })
  );
  return formatter.format(date);
};

// O dia civil LOCAL de um instante, como "2026-09-04". E a chave que decide se
// dois turnos precisam de um separador de dia entre eles. Local e nao UTC:
// uma conversa da uma da manha em Lisboa e de hoje para quem a le, e em UTC
// seria de ontem.
export const dayKey = (value) => {
  const date = value instanceof Date ? value : parse(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

// A data por extenso de um separador de dia, sem horas. "Hoje" e "ontem" nao
// saem daqui: sao chaves de traducao, para a maiuscula ser nossa e nao a que o
// Intl decidir em cada lingua.
export const formatDay = (iso, language) => {
  const date = parse(iso);
  if (!date) return "";
  const formatter = cached(
    "day",
    language,
    () => new Intl.DateTimeFormat(language, { day: "numeric", month: "long", year: "numeric" })
  );
  return formatter.format(date);
};

// Dia e mes abreviado, "4 set", para o chip de um intervalo de datas escolhido
// a mao. Aceita a chave de dia local ("2026-09-04") que os filtros guardam.
export const formatShortDay = (yyyyMmDd, language) => {
  if (!yyyyMmDd) return "";
  const date = new Date(`${yyyyMmDd}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const formatter = cached(
    "shortDay",
    language,
    () => new Intl.DateTimeFormat(language, { day: "numeric", month: "short" })
  );
  return formatter.format(date);
};

// So as horas e os minutos, para a coluna esquerda de cada turno.
export const formatClock = (iso, language) => {
  const date = parse(iso);
  if (!date) return "";
  const formatter = cached(
    "clock",
    language,
    () => new Intl.DateTimeFormat(language, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
  );
  return formatter.format(date);
};
